import React, { useState } from 'react';
import './RelayControl.css';

function RelayControlDetail({ imei, onBack }) {
  const [data, setData] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Constants based on user request
  const apiEndpoint = `${process.env.REACT_APP_TRACK_API_BASE }/testing3`;
  const apiKey = process.env.REACT_APP_API_KEY || (typeof window !== 'undefined' ? window.REACT_APP_API_KEY : '');

  React.useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        const now = new Date();
        const start7 = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        
        // Format dates as YYYYMMDDHHmmss
        const fmt = (d) => {
          const pad = (n) => String(n).padStart(2, '0');
          return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
        };
        
        const range7 = `${fmt(start7)},${fmt(now)}`;
        const url7 = `${apiEndpoint}?imeis=${encodeURIComponent(imei)}&TimeRange=${range7}`;
        const res7 = await fetch(url7, { headers: apiKey ? { 'x-api-key': apiKey } : undefined });
        if (!res7.ok) throw new Error('Failed to fetch data');
        const json = await res7.json();
        
        if (mounted) {
          let items = [];
          let payload = json;
          if (typeof payload?.body === 'string') {
            try { payload = JSON.parse(payload.body); } catch {}
          }
          if (Array.isArray(payload)) items = payload;
          else if (payload && Array.isArray(payload.data)) items = payload.data;

          if (items.length > 0) {
            const sorted = [...items].sort((a, b) => String(b.time_stamp || '').localeCompare(String(a.time_stamp || '')));
            const chosen = pickLatestWithStatus(sorted);
            setData(chosen || sorted[0]);
          } else {
            setData(null);
          }
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          console.error(err);
          setError('Failed to load device data');
          setLoading(false);
        }
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000); // Poll every 10s
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [imei]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const normalizeOnOff = (v, relayField) => {
    if (v === null || typeof v === 'undefined') return 'UNKNOWN';
    const s = String(v).trim().toUpperCase();
    if (s === 'ON' || s === 'OFF') return s;
    if (/^(1|0)$/.test(s)) {
      if (relayField) return s === '0' ? 'ON' : 'OFF';
      return s === '1' ? 'ON' : 'OFF';
    }
    return 'UNKNOWN';
  };

  const pickLatestWithStatus = (items) => {
    for (let i = 0; i < items.length; i++) {
      const st = getStatus(items[i]);
      if (st.ignition !== 'UNKNOWN' || st.relay !== 'UNKNOWN') return items[i];
    }
    return items[0] || null;
  };

  const getStatus = (item) => {
    if (!item) return { relay: 'UNKNOWN', ignition: 'UNKNOWN' };
    let ignition = 'UNKNOWN';
    let relay = 'UNKNOWN';
    
    const lower = Object.fromEntries(Object.keys(item).map(k => [k.toLowerCase(), item[k]]));
    const ignRaw = lower['ignition'] ?? lower['ign'] ?? lower['ign_status'] ?? lower['digitalin'] ?? lower['digital_input'] ?? lower['di'];
    const relRaw = lower['relay_status'] ?? lower['relay'] ?? lower['digitalout'] ?? lower['digital_output'] ?? lower['do'];
    const ignTry = normalizeOnOff(ignRaw, false);
    const relTry = normalizeOnOff(relRaw, true);
    if (ignTry !== 'UNKNOWN') ignition = ignTry;
    if (relTry !== 'UNKNOWN') relay = relTry;
    
    if (ignition === 'UNKNOWN' || relay === 'UNKNOWN') {
      const packetData = item.packet_data;
      if (packetData) {
        const parts = packetData.split(',');
        if (parts.length >= 8) {
          const cleanParts = parts.map(p => p.trim());
          const len = cleanParts.length;
          const digitalOutIndex = len - 6;
          const digitalInIndex = len - 7;
          const digitalOut = cleanParts[digitalOutIndex];
          const digitalIn = cleanParts[digitalInIndex];
          if (relay === 'UNKNOWN') {
            relay = digitalOut === '10' ? 'ON' : 'OFF';
          }
          if (ignition === 'UNKNOWN' && digitalIn && digitalIn.length > 0) {
            ignition = digitalIn.charAt(0) === '1' ? 'ON' : 'OFF';
          }
        }
      }
    }
    return { relay, ignition };
  };

  const { relay, ignition } = data ? getStatus(data) : { relay: 'UNKNOWN', ignition: 'UNKNOWN' };
  
  // Calculate online status
  // 5 minutes threshold
  const lastSeen = data ? new Date(String(data.time_stamp).replace(/(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1-$2-$3T$4:$5:$6Z')) : null;
  const isOnline = lastSeen && (new Date().getTime() - lastSeen.getTime() < 5 * 60 * 1000);
  const lastSeenIST = lastSeen ? lastSeen.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : null;

  const handleRelay = async (action) => {
    
  try {
    showToast(`Sending Relay ${action} command...`);

    const endpoint = 'https://rfid.mileedgelabs.com/relay';

    const controller = new AbortController();
    setTimeout(() => controller.abort(), 8000);

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ imei, action: action.toLowerCase() }),
      signal: controller.signal
    });

    if (!response.ok) {
      throw new Error(`Server error ${response.status}`);
    }

    const text = await response.text();

    let message = text;

    try {
      const json = JSON.parse(text);
      if (json.message) message = json.message;
    } catch {}

    showToast(message || `Relay ${action} command sent`);

  } catch (err) {
    console.error('Relay API Error:', err);
    showToast('Relay server unreachable');
  }
};
  
  return (
    <div className="details-page">
      <div className="details-header">
        <button className="back-btn" onClick={onBack}>
           <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
             <path d="M19 12H5M12 19l-7-7 7-7"/>
           </svg>
        </button>
      </div>
      
      <div className="details-card">
        <div className="device-header">
          <div className="device-info">
            <span className="label">IMEI</span>
            <span className="value">{imei}</span>
          </div>
        </div>

        <div className="status-grid">
          <div className="status-box">
            <div className="box-icon ignition">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
            </div>
            <div className="box-content">
              <span className="box-label">Ignition</span>
              <span className={`box-value ${ignition === 'ON' ? 'active' : 'inactive'}`}>
                {ignition}
              </span>
            </div>
          </div>

          <div className="status-box">
            <div className="box-icon relay">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M18.36 6.64a9 9 0 1 1-12.73 0M12 2v10"/>
              </svg>
            </div>
            <div className="box-content">
              <span className="box-label">Relay</span>
              <span className={`box-value ${relay === 'ON' ? 'active' : 'inactive'}`}>
                {relay}
              </span>
            </div>
          </div>
        </div>

        <div className="controls-section">
          <h3>Remote Controls</h3>
          <div className="control-group">
             <div className="control-label">Remote Engine Control</div>
             <div className="control-actions">
                <button 
                  className={`btn-control on ${relay === 'ON' ? 'active-state' : ''}`}
                  onClick={() => handleRelay('ON')}
                >
                  ON
                </button>
                <button 
                  className={`btn-control off ${relay === 'OFF' ? 'active-state' : ''}`}
                  onClick={() => handleRelay('OFF')}
                >
                  OFF
                </button>
             </div>
          </div>
        </div>
        
        {lastSeenIST && (
          <div className="last-seen">
            Last update: {lastSeenIST}
          </div>
        )}
      </div>
      
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

export default RelayControlDetail;